import React from 'react';
import { View } from 'react-native';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import EmptyState from '../emptyState/EmptyState';

const ErrorBoundaryFallback = ({ onRetry }) => {
	const styles = useThemedStyles(createStyles);

	return (
		<View style={styles.container}>
			<EmptyState
				icon="alert-circle-outline"
				message="Something went wrong"
				hint="The app hit an unexpected error. Your queued updates are safe."
				actionLabel="Try again"
				onActionPress={onRetry}
			/>
		</View>
	);
};

const createStyles = (theme) => ({
	container: {
		flex: 1,
		justifyContent: 'center',
		backgroundColor: theme.mainBg1,
	},
});

/**
 * Catches render errors anywhere below it so a single broken screen shows a
 * recoverable message instead of crashing the whole app. Retry remounts the
 * subtree from scratch.
 */
class ErrorBoundary extends React.Component {
	constructor (props) {
		super(props);
		this.state = { hasError: false };
		this.handleRetry = this.handleRetry.bind(this);
	}

	static getDerivedStateFromError () {
		return { hasError: true };
	}

	componentDidCatch (error, errorInfo) {
		console.error('[ErrorBoundary]', error, errorInfo?.componentStack);
	}

	handleRetry () {
		this.setState({ hasError: false });
	}

	render () {
		if (this.state.hasError) {
			return <ErrorBoundaryFallback onRetry={this.handleRetry} />;
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
